import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import MaterialTable from 'material-table';
import { Paper, Tooltip, IconButton, Typography, Fab } from '@material-ui/core';
import { Add, Refresh, Delete } from '@material-ui/icons';
import { hasPermission, BackButton, localization, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../sc-utilities';
import PrinterDelete from './PrinterDelete';
import * as actions from './store/printerActions';
import * as action from '../../np-core/Auth/store/authActions';
import Toolbar from '../../sc-utilities/Toolbar';
import { MuiTableRows } from '../../../themes';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const PrinterList = props => {
    const { classes, tableColumns, deleting, fetching, onFetchPrintersCount, onFetchPrinters, saveColumnsFilter, onBulkDeletePrinters, redirectHandler, activeCustomer, translations, permissions, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, printerIDs: [], forBulk: false });
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const selectionPermissions = hasAtLeastOnePermission(permissions, ['printer_delete']);
    const deletePermission = hasPermission(permissions, 'printer_delete');

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        selectionProps: rowData => {
            return (deletePermission) ? {
                id: `checkbox_${rowData.PrinterID}`
            } :
                {
                    disabled: true,
                    checked: false,
                    id: `checkbox_${rowData.PrinterID}`
                }
        },
        search: false,
        minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
        maxBodyHeight: window.innerHeight - 197,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }
    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomer;
        query['q'] = q;
        let count = await onFetchPrintersCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await onFetchPrinters(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const tableTitle = () => {
        return <div className={classes.tableTitle}>
            {BackButton(translations, classes.customTooltip)}
            <Typography className={classes.typography}>{translations['printers'] || 'Printers'}</Typography>
            {
                hasPermission(permissions, 'printer_create') ?
                    <Fab
                        id="create_printer"
                        variant="extended"
                        size="small"
                        color="primary"
                        style={{ borderRadius: '1px' }}
                        aria-label="Add"
                        className={classes.fab}
                        onClick={() => redirectHandler(`/settings/printer/create`)}
                    >
                        <Add size="small" />
                        {translations["create_new"] || "Create new"}
                    </Fab> : null
            }
            <Tooltip title={translations["refresh"] || "Refresh"}>
                <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                    <Refresh />
                </IconButton>
            </Tooltip>
        </div>
    }

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, printerIDs: [], forBulk: false });
    }

    const handleCloseDanger = async () => {
        await onBulkDeletePrinters({ PrinterIDs: deleteModalState.printerIDs, ActiveCustomerID: activeCustomer });
        setDeleteModalState({ open: false, printerIDs: [], forBulk: false });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleBulkDelete = printerIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, printerIDs: printerIDs, forBulk: true });
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["printers"] || "Printers"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(printer => printer.PrinterID))
        }
    ];

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        });
        objToSendColumn[`Printer`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomer })
        return newColumnState
    };

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar
                            data={props}
                            actions={tableActions}
                            selectedRows={props.selectedRows}
                            translations={translations}
                        />
                    )
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {
                hasPermission(permissions, 'printer_delete') ? (
                    <PrinterDelete
                        activeCustomer={activeCustomer}
                        entityName={translations['printers'] || 'Printers'}
                        translations={translations}
                        handleCloseDanger={handleCloseDanger}
                        handleCloseSafe={handleCloseSafe}
                        deleting={deleting}
                        deleteModalState={deleteModalState}
                    />
                ) : null
            }
        </Paper>
    )
}

const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        printers: state.setting.printers,
        deleting: state.setting.printers.deleting,
        fetching: state.setting.printers.fetching
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchPrintersCount: filter => dispatch(actions.fetchPrinterCount(filter)),
        onFetchPrinters: filter => dispatch(actions.fetchPrinter(filter)),
        onBulkDeletePrinters: data => dispatch(actions.bulkDeletePrinters(data)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data))
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(PrinterList));